// Productkiezer (Arctic Blue) — DOM-laag.
//
// sections/ab-productkiezer.liquid rendert alle schermen en tekstsjablonen;
// dit element toggelt schermen, houdt de antwoorden bij en vult de resultaat-
// slots met de vooraf gerenderde theme product cards uit de card-pool. De
// beslislogica zit in ab-productkiezer-engine.js en wordt dynamisch geladen
// via data-engine-src (versie-URL uit asset_url, dus geen importmap nodig).
import { Component } from "@theme/component";
import { formatCurrency } from "@theme/utilities";

class AbProductkiezer extends Component {
  requiredRefs = ["kzBar", "kzTeller", "kzTpl", "kzPool", "kzAanraderSlot"];

  /** @type {any} engine-module */
  #engine = null;
  /** @type {any} state */
  #S = null;
  /** @type {Record<string, number>} prijs in centen per handle */
  #prijzen = {};
  /** @type {Record<string, string>} titel per handle */
  #titels = {};
  #eersteRender = true;

  async connectedCallback() {
    super.connectedCallback();
    for (const card of this.#kaarten()) {
      this.#prijzen[card.dataset.handle] = Number(card.dataset.price) || 0;
      // Titel uit de tekst-span (vertaald door Weglot), data-title blijft NL.
      const titel = card.querySelector("[data-kz-titel]")?.textContent.trim();
      this.#titels[card.dataset.handle] = titel || card.dataset.title || card.dataset.handle;
    }

    try {
      this.#engine = await import(this.dataset.engineSrc);
    } catch (e) {
      console.error("Productkiezer: engine niet geladen", e);
      return;
    }

    this.#S = this.#engine.leegState();
    this.#prefill();
    if (this.#debug()) this.refs.kzLogicBtn?.removeAttribute("hidden");
    this.#render();
  }

  /* ---------- helpers ---------- */

  #debug() {
    if (this.dataset.debug === "true") return true;
    try { return new URLSearchParams(window.location.search).get("kiezerdebug") === "1"; } catch (e) { return false; }
  }

  /** ?wie=zwanger|kind|dier|volwassene slaat de eerste vraag over (landingspagina's/ads). */
  #prefill() {
    let wie = null;
    try { wie = new URLSearchParams(window.location.search).get("wie"); } catch (e) { /* niets */ }
    if (!["volwassene", "zwanger", "kind", "dier"].includes(wie || "")) return;
    this.#S.wie = wie;
    this.#S.stap = "wie";
    this.#engine.volgende(this.#S);
  }

  #kaarten() {
    return Array.from(this.querySelectorAll(".ab-kiezer__card[data-handle]"));
  }

  #t(key, tokens) {
    const el = this.refs.kzTpl.querySelector(`[data-tekst="${key}"]`);
    return this.#vul(el ? el.textContent : key, tokens);
  }

  #vul(tekst, tokens) {
    let uit = tekst || "";
    for (const [k, v] of Object.entries(tokens || {})) uit = uit.split("{" + k + "}").join(String(v));
    return uit;
  }

  #geld(cents) {
    if (cents === null || cents === undefined) return "";
    try { return formatCurrency(cents, this.dataset.moneyFormat); } catch (e) { return (cents / 100).toFixed(2); }
  }

  #gewichtLabel(id) {
    // Tekst-span eerst: vertaald door Weglot, data-label blijft NL.
    const tpl = this.refs.kzTpl.querySelector(`[data-tekst="gewicht_${id.replace("-", "_")}"]`);
    const knop = this.querySelector(`[data-q="gewicht"][data-v="${id}"]`);
    if (knop) return (tpl?.textContent || knop.dataset.label || knop.textContent).trim().toLowerCase();
    return tpl ? tpl.textContent : id;
  }

  /** Tokens uit de engine (centen, gewicht-id's) omzetten naar leesbare waarden. */
  #tokens(tokens) {
    const uit = {};
    for (const [k, v] of Object.entries(tokens || {})) {
      if (k.startsWith("prijs")) uit[k] = this.#geld(v);
      else if (k === "gewicht") uit[k] = this.#gewichtLabel(v);
      else uit[k] = v;
    }
    return uit;
  }

  /* ---------- acties (on:click) ---------- */

  kies(event) {
    if (!this.#S) return;
    const knop = event.target.closest("[data-q]");
    if (!knop) return;
    this.#S[knop.dataset.q] = knop.dataset.v;
    this.#S.gewisseld = false;
    this.#engine.volgende(this.#S);
    this.#render();
  }

  overslaan() {
    if (!this.#S) return;
    this.#engine.volgende(this.#S);
    this.#render();
  }

  vorige() {
    if (!this.#S) return;
    if (this.#engine.vorige(this.#S)) this.#render();
  }

  opnieuw() {
    if (!this.#engine) return;
    this.#S = this.#engine.leegState();
    this.#render();
  }

  naarBorstvoeding() {
    if (!this.#engine) return;
    this.#S = this.#engine.leegState();
    this.#S.wie = "zwanger";
    this.#S.pad = ["wie"];
    this.#S.stap = "bron";
    this.#render();
  }

  wissel(aan) {
    if (!this.#S) return;
    this.#S.gewisseld = aan === true;
    this.#render(false);
  }

  toggleLogica() {
    const box = this.refs.kzLogica;
    if (!box) return;
    box.hidden = !box.hidden;
    if (this.refs.kzLogicBtn) this.refs.kzLogicBtn.textContent = this.#t(box.hidden ? "nav_logic_show" : "nav_logic_hide");
    this.#logica();
  }

  async mailAdvies(event) {
    event.preventDefault();
    const input = this.refs.kzEmail;
    const fout = this.refs.kzEmailFout;
    const ok = this.refs.kzEmailOk;
    const email = (input?.value || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (fout) fout.hidden = false;
      return;
    }
    if (fout) fout.hidden = true;
    const key = this.dataset.klaviyoKey;
    if (!key) return;
    const r = this.#engine.berekenAdvies(this.#S, this.#prijzen);
    const antwoorden = ["wie", "leeftijd", "bron", "vorm", "vis", "zon", "multi", "gewicht"]
      .filter((k) => this.#S[k] !== null)
      .map((k) => k + "=" + this.#S[k])
      .join(",");
    try {
      await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=" + encodeURIComponent(key), {
        method: "POST",
        headers: { "Content-Type": "application/json", revision: "2024-10-15" },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email,
                    properties: {
                      productkiezer_route: this.#engine.pad(this.#S).join(">"),
                      productkiezer_antwoorden: antwoorden,
                      productkiezer_aanrader: r.aanrader,
                      productkiezer_alternatief: r.alternatief ? r.alternatief.handle : "",
                    },
                  },
                },
              },
            },
          },
        }),
      });
    } catch (e) {
      /* stil: het advies staat al op het scherm */
    }
    if (ok) ok.hidden = false;
    if (input) input.value = "";
  }

  /* ---------- render ---------- */

  #render(scroll = true) {
    const S = this.#S;

    // Schermen: één [data-stap] zichtbaar, bij varianten de juiste.
    for (const scherm of this.querySelectorAll("[data-stap]")) {
      let toon = scherm.dataset.stap === S.stap;
      if (toon && scherm.dataset.variant) {
        const variant = S.stap === "vis"
          ? (S.wie === "zwanger" ? "zwanger" : "default")
          : (S.wie === "dier" ? "dier" : (S.wie === "kind" && S.leeftijd === "3-11" ? "kind" : "default"));
        toon = scherm.dataset.variant === variant;
      }
      scherm.hidden = !toon;
    }

    // Gekozen antwoorden markeren.
    for (const knop of this.querySelectorAll("[data-q]")) {
      knop.setAttribute("aria-pressed", String(S[knop.dataset.q] === knop.dataset.v));
    }

    // Voortgang.
    const v = this.#engine.voortgang(S);
    this.refs.kzBar.style.width = v.pct + "%";
    if (v.type === "vraag") {
      this.refs.kzTeller.textContent = this.#t("progress", { n: v.nr, total: v.totaal });
    } else {
      this.refs.kzTeller.textContent = this.#t(v.type === "resultaat" ? "progress_result" : "progress_none");
    }

    if (S.stap === "resultaat") this.#renderResultaat();
    else this.#kaartenTerug();

    this.#logica();

    if (scroll && !this.#eersteRender) {
      window.scrollTo({ top: this.offsetTop - 90, behavior: "smooth" });
    }
    this.#eersteRender = false;
  }

  #kaartenTerug() {
    const pool = this.refs.kzPool;
    for (const card of this.#kaarten()) {
      card.hidden = true;
      if (card.parentElement !== pool) pool.appendChild(card);
    }
    for (const slot of [this.refs.kzAanraderSlot, this.refs.kzAltSlot, this.refs.kzAddonSlot]) {
      if (slot) slot.querySelectorAll(".ab-kiezer__card--fallback").forEach((n) => n.remove());
    }
  }

  /** Zet de kaart van een handle in een slot (uit de pool, anders via de Ajax API). */
  #plaats(slot, handle) {
    if (!slot) return;
    const card = this.#kaarten().find((c) => c.dataset.handle === handle);
    if (card) {
      card.hidden = false;
      slot.appendChild(card);
      return;
    }
    console.warn("Productkiezer: product niet in de card-pool, val terug op Ajax API:", handle);
    const fallback = document.createElement("div");
    fallback.className = "ab-kiezer__card ab-kiezer__card--fallback";
    slot.appendChild(fallback);
    fetch("/products/" + handle + ".js")
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) { fallback.remove(); return; }
        const beeld = p.featured_image || (p.images && p.images[0]);
        fallback.innerHTML =
          (beeld ? '<a href="' + p.url + '"><img src="' + beeld + '&width=480" alt="" width="480" height="480" loading="lazy"></a>' : "") +
          '<a href="' + p.url + '">' + p.title + "</a>" +
          "<span>" + this.#geld(p.price) + "</span>";
        this.#prijzen[handle] = p.price;
        this.#titels[handle] = p.title;
      })
      .catch(() => fallback.remove());
  }

  #renderResultaat() {
    const S = this.#S;
    const E = this.#engine;
    const r = E.berekenAdvies(S, this.#prijzen);
    const refs = this.refs;

    this.#kaartenTerug();

    // Onderbouwing.
    refs.kzRedenen.replaceChildren();
    for (const reden of r.redenen) {
      const tpl = refs.kzTpl.querySelector(`[data-reden="${reden.key}"]`);
      if (!tpl) continue;
      const p = tpl.cloneNode(true);
      p.textContent = this.#vul(tpl.textContent, this.#tokens(reden.tokens));
      refs.kzRedenen.appendChild(p);
    }

    // Aanrader.
    refs.kzEyebrow.textContent = this.#t(r.eyebrow === "aanvullen" ? "result_eyebrow_aanvullen" : "result_eyebrow");
    this.#plaats(refs.kzAanraderSlot, r.aanrader);

    // Praktijkblok (alleen hond/kat).
    if (r.praktijk) {
      const pr = r.praktijk;
      let html = "";
      if (pr.type === "caps") {
        html = "<strong>" + this.#t("praktijk_heading") + "</strong>" +
          this.#t(pr.caps === 1 ? "praktijk_dier_caps_1" : "praktijk_dier_caps", { caps: pr.caps, dagen: pr.dagen, prijs: this.#geld(pr.prijs) });
      } else if (pr.type === "olie") {
        html = "<strong>" + this.#t("praktijk_heading") + "</strong>" +
          this.#t("praktijk_dier_olie", { ml: pr.ml, dagen: pr.dagen, prijs: this.#geld(pr.prijs) }) +
          "<br>" + this.#t("praktijk_dier_koelkast");
      } else {
        html = "<strong>" + this.#t("praktijk_dier_zwaar_heading") + "</strong>" +
          pr.rijen.map((rij) => this.#t("praktijk_dier_zwaar_regel", { gewicht: this.#gewichtLabel(rij.id), ml: rij.ml, dagen: rij.dagen })).join("<br>") +
          "<br>" + this.#t("praktijk_dier_zwaar_50") + "<br>" + this.#t("praktijk_dier_zwaar_koelkast");
      }
      refs.kzPraktijk.innerHTML = html;
      refs.kzPraktijk.hidden = false;
    } else {
      refs.kzPraktijk.hidden = true;
    }

    // Wissel naar de variant zonder vitamine D.
    if (r.wissel) {
      refs.kzWissel.hidden = false;
      if (S.gewisseld) {
        refs.kzWisselTekst.textContent = this.#t("wissel_done");
        refs.kzWisselBtn.hidden = true;
        refs.kzWisselUndo.hidden = false;
      } else {
        refs.kzWisselTekst.textContent = this.#t("wissel_text", { product: this.#titels[r.wissel] || r.wissel, prijs: this.#geld(this.#prijzen[r.wissel]) });
        refs.kzWisselBtn.hidden = false;
        refs.kzWisselUndo.hidden = true;
      }
    } else {
      refs.kzWissel.hidden = true;
    }

    // Alternatief.
    if (r.alternatief) {
      refs.kzAlt.hidden = false;
      this.#plaats(refs.kzAltSlot, r.alternatief.handle);
      const why = refs.kzTpl.querySelector(`[data-why="${r.alternatief.why.key}"]`);
      refs.kzAltWhy.textContent = this.#vul(why ? why.textContent : "", this.#tokens(r.alternatief.why.tokens));
    } else {
      refs.kzAlt.hidden = true;
    }

    // Aanvulling: combinatie gaat vóór D3 + K2 los erbij.
    const d3 = this.#titels["vitamine-d3-k2"] || "Vitamine D3 + K2";
    if (r.combo) {
      const hoog = E.P.visHoog.h;
      const samen = (this.#prijzen[hoog] || 0) + (this.#prijzen["vitamine-d3-k2"] || 0);
      refs.kzAddon.hidden = false;
      refs.kzAddonKop.textContent = this.#t("combo_heading");
      refs.kzAddonTekst.textContent = this.#t("combo_text", { product_hoog: this.#titels[hoog] || hoog, product_d3: d3, prijs: this.#geld(samen) });
      refs.kzAddonWarn.textContent = this.#t("addon_warning", { product: d3 });
      this.#plaats(refs.kzAddonSlot, "vitamine-d3-k2");
    } else if (r.d3k2) {
      refs.kzAddon.hidden = false;
      refs.kzAddonKop.textContent = this.#t("addon_heading");
      refs.kzAddonTekst.textContent = this.#t("addon_text", { product: d3 });
      refs.kzAddonWarn.textContent = this.#t("addon_warning", { product: d3 });
      this.#plaats(refs.kzAddonSlot, "vitamine-d3-k2");
    } else {
      refs.kzAddon.hidden = true;
    }

    // Meetregel en verloskundige-regel.
    if (refs.kzMetenIndex) refs.kzMetenIndex.hidden = r.meten !== "index";
    if (refs.kzMetenVitd) refs.kzMetenVitd.hidden = r.meten !== "vitd";
    if (refs.kzCross) refs.kzCross.hidden = !r.cross;

    // E-mail (alleen met Klaviyo-key).
    if (refs.kzMail) {
      refs.kzMail.hidden = !this.dataset.klaviyoKey;
      if (refs.kzEmailOk) refs.kzEmailOk.hidden = true;
      if (refs.kzEmailFout) refs.kzEmailFout.hidden = true;
    }
  }

  #logica() {
    const box = this.refs.kzLogica;
    if (!box || box.hidden || !this.#engine) return;
    box.textContent = this.#engine.logica(this.#S, this.#prijzen);
  }
}

if (!customElements.get("ab-productkiezer")) customElements.define("ab-productkiezer", AbProductkiezer);
